import { useState } from 'react';
import { Button, Input, Textarea, Select } from '@travellegacy/nexops-design-system';
import { useCreateReminder } from '../hooks/useReminders';
import type { CreateReminderData } from '../services/reminders.service';

interface ReminderFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
}

const NOTIFY_BEFORE_OPTIONS: { value: string; label: string }[] = [
  { value: '5', label: '5 minutos antes' },
  { value: '15', label: '15 minutos antes' },
  { value: '30', label: '30 minutos antes' },
  { value: '60', label: '1 hora antes' },
  { value: '120', label: '2 horas antes' },
];

export function ReminderForm({ onSuccess, onCancel }: ReminderFormProps) {
  const createReminder = useCreateReminder();

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    reminderDate: '',
    reminderTime: '',
    notifyBefore: '30',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validações
    if (!formData.title.trim()) {
      return;
    }

    if (!formData.reminderDate || !formData.reminderTime) {
      return;
    }

    // Combinar data e hora
    const reminderDateTime = new Date(`${formData.reminderDate}T${formData.reminderTime}`);

    // Validar se é data futura
    if (reminderDateTime <= new Date()) {
      alert('A data/hora do lembrete deve ser no futuro');
      return;
    }

    const data: CreateReminderData = {
      title: formData.title,
      description: formData.description || undefined,
      reminderDateTime: reminderDateTime.toISOString(),
      notifyBefore: Number(formData.notifyBefore) as 5 | 15 | 30 | 60 | 120,
    };

    createReminder.mutate(data, {
      onSuccess: () => {
        setFormData({
          title: '',
          description: '',
          reminderDate: '',
          reminderTime: '',
          notifyBefore: '30',
        });
        onSuccess?.();
      },
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Título */}
      <div>
        <label htmlFor="title" className="block text-sm font-medium mb-1.5">
          Título *
        </label>
        <Input
          id="title"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          placeholder="Ex: Reunião com cliente"
          required
          maxLength={200}
        />
      </div>

      {/* Descrição */}
      <div>
        <label htmlFor="description" className="block text-sm font-medium mb-1.5">
          Descrição (opcional)
        </label>
        <Textarea
          id="description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="Detalhes adicionais sobre o lembrete..."
          rows={3}
          maxLength={1000}
        />
      </div>

      {/* Data e Hora */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="reminderDate" className="block text-sm font-medium mb-1.5">
            Data *
          </label>
          <Input
            type="date"
            id="reminderDate"
            value={formData.reminderDate}
            onChange={(e) => setFormData({ ...formData, reminderDate: e.target.value })}
            required
          />
        </div>

        <div>
          <label htmlFor="reminderTime" className="block text-sm font-medium mb-1.5">
            Hora *
          </label>
          <Input
            type="time"
            id="reminderTime"
            value={formData.reminderTime}
            onChange={(e) => setFormData({ ...formData, reminderTime: e.target.value })}
            required
          />
        </div>
      </div>

      {/* Notificar Antes */}
      <div>
        <Select
          label="Notificar antes *"
          value={formData.notifyBefore}
          onChange={(e) => setFormData({ ...formData, notifyBefore: e.target.value })}
          options={NOTIFY_BEFORE_OPTIONS}
          required
        />
      </div>

      {/* Botões */}
      <div className="flex justify-end gap-3 pt-4">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancelar
          </Button>
        )}
        <Button type="submit" disabled={createReminder.isPending}>
          {createReminder.isPending ? 'Criando...' : 'Criar Lembrete'}
        </Button>
      </div>
    </form>
  );
}
